import axios from "axios";
import { BASE_URL } from "../../global";

import {
  getDataFromSqlJs,
  getDataFromUrlSqlJs,
} from "../../offlineApp/offline/fetchHandlers";
import {
  createOrUpdateTableData,
  executeSqlInSqlJs,
} from "../../offlineApp/offline/submitHandlers";

const historyData =
  JSON.parse(sessionStorage.getItem("historicalTable")) || false;

const tables = [
  "datasheetStaticReadings",
  "certificates",
  "datasheets",
  "audit_log",
  "srfInstruments",
  "srfAnalytics",
  "attendance",
  "srfs",
  "certificatesView",
  "quotation",
  "customInvoice",
  "productDetails",
  "paymentMaster"
];

// Define the allowed table list
const tableList = [
  "instruments",
  "srfInstruments",
  "settings",
  "unit",
  "discipline",
  "clients",
  "branch",
  "certificates",
  "csrfForm",
  "addresses",
  "attendance",
  "userClientAssign",
  "users",
];

let sqlExecutionLock = Promise.resolve();

const axiosWithToken = axios.create({
  baseURL: BASE_URL,
});

// Utility functions
const modifyUrlForHistory = (url) => {
  if (!url) return url;
  let modifiedUrl = url;
  tables.forEach((table) => {
    const regex = new RegExp(`(?<!\\w)${table}(?!\\w)`);
    if (regex.test(modifiedUrl) && !modifiedUrl.includes(`${table}_hist`)) {
      modifiedUrl = modifiedUrl.replace(regex, `${table}_hist`);
    }
  });
  return modifiedUrl;
};

// ✅ Modified query handler with isCopySRF logic
const modifyQueryForHistory = (query, isCopySRF = false) => {
  let modifiedQuery = query;

  tables.forEach((table) => {
    const regex = new RegExp(`\\b${table}\\b`, "g");

    // 🧩 Skip certain replacements when copying SRF
    if (isCopySRF) {
      // Skip replacement for queries selecting serviceReqNumber
      if (/select\s+serviceReqNumber\s+from/i.test(modifiedQuery)) return;
      // Skip replacement for INSERT INTO statements
      if (/^\s*insert\s+into/i.test(modifiedQuery)) return;
    }

    modifiedQuery = modifiedQuery.replace(regex, `${table}_hist`);
  });

  return modifiedQuery;
};

// 🔧 Axios interceptor
axiosWithToken.interceptors.request.use(async (config) => {
  const isOffLine = localStorage.getItem("isOffLine") === "true";
  const isCopySRF = localStorage.getItem("isCopySRF") === "true";

  const token = sessionStorage.getItem("authToken");
  const historyData =
    JSON.parse(sessionStorage.getItem("historicalTable")) || false;

  if (token) {
    config.headers = {
      ...config.headers,
      authorization: `Bearer ${token}`,
    };
  }

  // ✅ Apply history table replacement for URLs
  if (historyData && !isCopySRF && config?.url) {
    config.url = modifyUrlForHistory(config.url);
  }

  // ✅ Apply query-level replacements for dynamic URLs
  if (config?.url?.endsWith("dynamic") && historyData && config?.data?.query) {
    config.data.query = modifyQueryForHistory(config.data.query, isCopySRF);
  }

  // ✅ Mark config for offline mock processing
  if (isOffLine) {
    config.__isOffline = true;
    return Promise.reject({ config: config, message: "Offline mode enabled." });
  }

  return config;
});

// 🔄 Response interceptor (offline fallback)
axiosWithToken.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (config?.__isOffline) {
      const isDynamicFetchData = config.url?.endsWith("dynamic");
      const historyData =
        JSON.parse(sessionStorage.getItem("historicalTable")) || false;
      const isCopySRF = localStorage.getItem("isCopySRF") === "true";

      try {
        // 🧩 Handle dynamic SQL queries
        if (isDynamicFetchData && config.data?.query) {
          let sqlQuery = config.data.query;
          if (historyData) {
            sqlQuery = modifyQueryForHistory(sqlQuery, isCopySRF);
          }

          const usedTables = Array.from(
            new Set(sqlQuery.match(/\b\w+\b/g))
          ).filter((word) => tableList.includes(word));

          const isValidTablesOnly = usedTables.every((t) =>
            tableList.includes(t)
          );

          if (isValidTablesOnly) {
            const isSelectQuery = /^\s*select/i.test(sqlQuery.trim());
            let result;

            if (isSelectQuery) {
              const data = await getDataFromSqlJs(sqlQuery);
              result = {
                data,
                status: 200,
                statusText: "OK",
                config,
                headers: {},
              };
            } else {
              const success = await (sqlExecutionLock = sqlExecutionLock.then(
                () => executeSqlInSqlJs(sqlQuery)
              ));
              result = {
                data: { success },
                status: success ? 200 : 500,
                statusText: success ? "OK" : "Execution Failed",
                config,
                headers: {},
              };
            }

            return result;
          }
        }

        // 🧩 Handle regular GET requests
        if (config.method?.toLowerCase() === "get") {
          const offlineUrl = historyData
            ? modifyUrlForHistory(config.url)
            : config.url;
          const { found, data, message } = await getDataFromUrlSqlJs(
            offlineUrl
          );

          if (found) {
            return {
              data,
              status: 200,
              statusText: "OK",
              config,
              headers: {},
            };
          } else {
            return Promise.reject({ message: "Offline mode: " + message });
          }
        }

        // 🧩 Handle write operations
        if (
          ["put", "patch", "post", "delete"].includes(
            config.method?.toLowerCase()
          )
        ) {
          const submitedData = await (sqlExecutionLock = sqlExecutionLock.then(
            () => createOrUpdateTableData(config.url, config.data, config.method)
          ));
          return submitedData;
        }
      } catch (err) {
        return Promise.reject({
          message: "Offline request failed",
          error: err,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default axiosWithToken;
